import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Shield,
  CheckCircle,
  Users,
  Scale,
  Sparkles,
  Clock,
  MapPin,
  Wifi,
  Bell
} from 'lucide-react';
import { safetyFeatures, futureFeature, busyDaysFeatures } from '../data/mock';

const SafetyPage = () => {
  const safetyIconMap = {
    seatbelt: Shield,
    weight: Scale,
    users: Users,
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden jolly-hero-gradient py-16 md:py-24">
        <div className="absolute top-10 right-10 w-20 h-20 rounded-full bg-[hsl(45,98%,80%)] opacity-50 sun-pulse" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold">
                <span className="jolly-text-orange">Safety First.</span>
                <br />
                <span className="jolly-text-teal">Always.</span>
              </h1>
              <p className="text-gray-600 text-lg md:text-xl max-w-xl">
                Every Jolly Tots Ride is designed with your child's safety as the top priority. Here's how we keep your little ones secure.
              </p>
            </div>
            
            {/* Safety Illustration */}
            <div className="relative">
              <div className="bg-white rounded-3xl p-8 shadow-xl">
                <div className="text-center">
                  <div className="inline-block p-6 bg-[hsl(120,60%,90%)] rounded-full mb-4">
                    <Shield className="w-16 h-16 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold jolly-text-teal mb-2">Safety Certified</h3>
                  <p className="text-gray-600">All equipment meets strict safety standards</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Features Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="jolly-section-title">Every Jolly Tots Ride includes:</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {safetyFeatures.map((feature, index) => {
              const Icon = safetyIconMap[feature.icon];
              return (
                <Card key={feature.id} className="jolly-card">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="step-badge bg-[hsl(33,100%,50%)] text-white">
                          {index + 1}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold jolly-text-teal mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sanitisation Section */}
      <section className="py-16 md:py-24 jolly-gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
                <div className="flex items-center justify-center gap-6">
                  <div className="text-center">
                    <div className="inline-block p-4 bg-[hsl(200,70%,90%)] rounded-full mb-3">
                      <Sparkles className="w-10 h-10 text-[hsl(200,70%,50%)]" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Sanitised</p>
                  </div>
                  <div className="text-4xl">→</div>
                  <div className="text-center">
                    <div className="inline-block p-4 bg-[hsl(120,60%,90%)] rounded-full mb-3">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Ready</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2 space-y-6">
              <h2 className="jolly-section-title">Hygiene & Sanitisation</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Every trolley is thoroughly sanitised after each use. Our cleaning protocol includes:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Surface disinfection with child-safe products</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Harness and seatbelt cleaning</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Wheel and handle sanitisation</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Regular deep cleaning schedule</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Feature Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-dashed border-[hsl(210,90%,70%)] bg-[hsl(210,90%,97%)]">
            <CardContent className="p-8 md:p-12">
              <div className="text-center space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[hsl(210,90%,55%)] text-white rounded-full text-sm font-semibold">
                  <Clock className="w-4 h-4" />
                  Coming Soon
                </div>
                <h2 className="text-2xl md:text-3xl font-bold jolly-text-teal">
                  {futureFeature.title}
                </h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                  {futureFeature.description}
                </p>
                <div className="grid sm:grid-cols-3 gap-6 pt-4">
                  <div className="text-center">
                    <div className="inline-block p-3 bg-white rounded-xl shadow-sm mb-2">
                      <Wifi className="w-8 h-8 jolly-text-blue" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">Real-time Tracking</p>
                  </div>
                  <div className="text-center">
                    <div className="inline-block p-3 bg-white rounded-xl shadow-sm mb-2">
                      <MapPin className="w-8 h-8 jolly-text-blue" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">Location Alerts</p>
                  </div>
                  <div className="text-center">
                    <div className="inline-block p-3 bg-white rounded-xl shadow-sm mb-2">
                      <Bell className="w-8 h-8 jolly-text-blue" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">Peace of Mind</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Busy Days Section */}
      <section className="py-16 md:py-24 jolly-gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="jolly-section-title">Designed for Busy Shopping Days:</h2>
              <div className="space-y-4">
                {busyDaysFeatures.map((feature) => (
                  <div key={feature.id} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[hsl(33,100%,50%)] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Illustration */}
            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <div className="flex items-center justify-center">
                <div className="relative">
                  {/* Multiple trolleys illustration */}
                  <div className="flex items-end gap-3">
                    <div className="w-16 h-12 bg-[hsl(210,70%,30%)] rounded-lg"></div>
                    <div className="w-16 h-12 bg-[hsl(33,100%,50%)] rounded-lg"></div>
                    <div className="w-16 h-12 bg-[hsl(120,50%,40%)] rounded-lg"></div>
                  </div>
                  <p className="text-center mt-4 text-sm font-medium text-gray-600">
                    Fleet ready for peak times
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-24 bg-[hsl(195,80%,25%)] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Trusted by Families. Designed for Malls.
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Our commitment to safety has earned the trust of families and mall partners across South Africa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/parents">
              <Button className="jolly-btn-orange">
                For Parents
              </Button>
            </Link>
            <Link to="/partners">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold rounded-full px-6 py-3">
                For Mall Partners
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SafetyPage;
